// Third-party partner activities — real data + FareHarbor calendar embeds.
// Each activity has a dedicated page at /partner/:slug with the FareHarbor
// availability calendar embedded inline so guests book right on the page.
// gallery / highlights / included / duration are PLACEHOLDER content the
// owner will refine with real Hacienda Carey / partner media.
import { saltyExperiences, SALTY_SPIRIT } from "@/data/salty-spirit";

const U = (id) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=65&w=850`;
const FS = (id) => `https://cdn.filestackcontent.com/${id}`;

export const CATEGORIES = ["water", "land", "family", "food"];

// FareHarbor inline booking-calendar URL (SPA-safe iframe — no document.write).
// asn-ref / ref = haciendacarey tracks our affiliate commission.
export function calendarIframeSrc(shortname, itemId) {
  return `https://fareharbor.com/embeds/book/${shortname}/items/${itemId}/calendar/?full-items=no&flow=no&asn=fhdn&asn-ref=haciendacarey&ref=haciendacarey&bookable-only=yes`;
}

export const partners = [
  {
    slug: "el-yunque-luquillo-combo",
    category: "land",
    shortname: "accesstourspr",
    itemId: "8752",
    cover: FS("Ih1QyQumRVCP10xjwPlH"),
    gallery: [FS("Ih1QyQumRVCP10xjwPlH"), U("1441974231531-c6227db76b6e"), U("1520962922320-2038eebab146"), U("1596797038530-2c107229654b")],
    place: "Fajardo · El Yunque",
    company: { name: "Puerto Rico Access Tours", email: "accesstourspr@gmail.com", phone: "1-787-421-1800" },
    en: {
      name: "El Yunque Rainforest & Luquillo Kiosks Combo",
      tag: "Rainforest + food",
      duration: "Full day (~6 hrs)",
      short: "The only tropical rainforest in the U.S. Forest System, plus a stop at the famous Luquillo food kiosks.",
      description: "Hike to waterfalls and lookout towers inside El Yunque National Forest — the only tropical rainforest managed by the U.S. Forest Service — then cool down with authentic local bites at the legendary Luquillo Kiosks. A full day of nature and flavor on Puerto Rico's east coast.",
      highlights: ["Waterfalls & lookout towers", "Only U.S. tropical rainforest", "Luquillo food-kiosk stop", "Small-group guided hike"],
      included: ["Bilingual local guide", "Park entry coordination", "Round-trip from meeting point"],
    },
    es: {
      name: "Combo El Yunque y Kioscos de Luquillo",
      tag: "Selva + comida",
      duration: "Día completo (~6 hrs)",
      short: "El único bosque tropical del Sistema Forestal de EE. UU., más una parada en los famosos kioscos de Luquillo.",
      description: "Camina hasta cascadas y torres de observación dentro del Bosque Nacional El Yunque — el único bosque tropical del Servicio Forestal de EE. UU. — y luego refréscate con auténtica comida local en los legendarios Kioscos de Luquillo. Un día completo de naturaleza y sabor en la costa este de Puerto Rico.",
      highlights: ["Cascadas y torres de observación", "Único bosque tropical de EE. UU.", "Parada en kioscos de Luquillo", "Caminata guiada en grupo pequeño"],
      included: ["Guía local bilingüe", "Coordinación de entrada al parque", "Transporte ida y vuelta"],
    },
  },
  {
    slug: "san-juan-sea-trek",
    category: "water",
    shortname: "aquaadventurepr",
    itemId: "11467",
    cover: FS("vQLXoaJfQwqHkK5CzltO"),
    gallery: [FS("vQLXoaJfQwqHkK5CzltO"), U("1530053969600-caed2596d242"), U("1583212292454-1fe6229603b7"), U("1607153333879-c174d265f1d2")],
    place: "San Juan",
    company: { name: "Aqua Adventures Puerto Rico", email: "info@aquaadventurepr.com", phone: "787.636.8811" },
    en: {
      name: "San Juan Sea Trek Underwater Walk",
      tag: "Walk the seafloor",
      duration: "~2 hrs",
      short: "Stroll along the ocean floor in a special helmet — no swimming or scuba certification needed.",
      description: "Descend beneath the surface and walk along the seafloor wearing a specialized helmet that keeps you breathing normally the whole time. No swimming skills or scuba certification required — just an unforgettable face-to-face encounter with tropical fish and reef life off San Juan.",
      highlights: ["Walk the seafloor in a helmet", "No swimming skills needed", "Breathe normally underwater", "Tropical fish up close"],
      included: ["Sea Trek helmet & gear", "Certified dive guide", "Safety briefing"],
    },
    es: {
      name: "Caminata Submarina Sea Trek San Juan",
      tag: "Camina el fondo marino",
      duration: "~2 hrs",
      short: "Camina por el fondo del mar con un casco especial — sin nadar ni certificación de buceo.",
      description: "Desciende bajo la superficie y camina por el fondo marino con un casco especial que te permite respirar normalmente todo el tiempo. No necesitas saber nadar ni tener certificación de buceo — solo un encuentro inolvidable cara a cara con los peces tropicales y la vida del arrecife frente a San Juan.",
      highlights: ["Camina el fondo con casco", "No necesitas saber nadar", "Respira normal bajo el agua", "Peces tropicales de cerca"],
      included: ["Casco y equipo Sea Trek", "Guía de buceo certificado", "Charla de seguridad"],
    },
  },
  {
    slug: "el-morro-walking-tour",
    category: "family",
    shortname: "patriatourspuertorico",
    itemId: "106812",
    cover: FS("ttrSb7VrQjmjJViaiU86"),
    gallery: [FS("ttrSb7VrQjmjJViaiU86"), U("1559128010-7c1ad6e1b6a5"), U("1507525428034-b723cf961d3e"), U("1471922694854-ff1b63b20054")],
    place: "Old San Juan",
    company: { name: "Patria Tours Puerto Rico", email: "info@patria-tours.com", phone: "1 (787) 923-6885" },
    en: {
      name: "Inside El Morro Castle Walking Tour",
      tag: "500 years of history",
      duration: "~1.5 hrs",
      short: "A guided walk through the 16th-century fortress guarding Old San Juan's harbor.",
      description: "Explore the storied halls, ramparts and tunnels of Castillo San Felipe del Morro with a local historian. Discover how this UNESCO World Heritage fortress defended San Juan Bay for centuries, and take in sweeping Atlantic views from the cannon-lined walls.",
      highlights: ["16th-century seaside fortress", "UNESCO World Heritage site", "Atlantic cannon-wall views", "Local historian guide"],
      included: ["Expert walking guide", "Historical storytelling", "Photo stops"],
    },
    es: {
      name: "Tour a Pie por el Castillo El Morro",
      tag: "500 años de historia",
      duration: "~1.5 hrs",
      short: "Un recorrido guiado por la fortaleza del siglo XVI que resguarda la bahía del Viejo San Juan.",
      description: "Recorre los salones, murallas y túneles del Castillo San Felipe del Morro junto a un historiador local. Descubre cómo esta fortaleza Patrimonio de la Humanidad de la UNESCO defendió la bahía de San Juan durante siglos y disfruta las vistas del Atlántico desde sus murallas.",
      highlights: ["Fortaleza del siglo XVI", "Patrimonio de la UNESCO", "Vistas del Atlántico", "Guía historiador local"],
      included: ["Guía experto a pie", "Relato histórico", "Paradas para fotos"],
    },
  },
  {
    slug: "san-juan-jet-snorkeling",
    category: "water",
    shortname: "jet-snorkel",
    itemId: "168224",
    cover: FS("1nQWdTS3ax3unNpCzXGQ"),
    gallery: [FS("1nQWdTS3ax3unNpCzXGQ"), U("1583212292454-1fe6229603b7"), U("1682687982501-1e58ab814714"), U("1544551763-46a013bb70d5")],
    place: "San Juan",
    company: { name: "Jet Snorkel", email: "jetsnorkel@gmail.com", phone: "(704) 313-8007" },
    en: {
      name: "Jet Snorkeling Adventure",
      tag: "Snorkel + jet power",
      duration: "~2 hrs",
      short: "Cover more reef with a motorized underwater scooter that pulls you effortlessly along.",
      description: "Glide across San Juan's reefs powered by a handheld underwater jet scooter. See more marine life with far less effort — perfect for snorkelers of every level who want a bigger adventure and better fish-spotting.",
      highlights: ["Motorized underwater scooter", "Cover more reef, less effort", "All snorkel levels welcome", "More fish sightings"],
      included: ["Jet scooter & snorkel gear", "Guide & safety vest", "Briefing & practice"],
    },
    es: {
      name: "Aventura de Jet Snorkeling",
      tag: "Snorkel + motor",
      duration: "~2 hrs",
      short: "Recorre más arrecife con un scooter submarino motorizado que te lleva sin esfuerzo.",
      description: "Deslízate por los arrecifes de San Juan impulsado por un scooter submarino de mano. Observa más vida marina con mucho menos esfuerzo — perfecto para snorkelistas de todos los niveles que quieren una aventura mayor.",
      highlights: ["Scooter submarino motorizado", "Más arrecife, menos esfuerzo", "Todos los niveles", "Más avistamientos de peces"],
      included: ["Scooter y equipo de snorkel", "Guía y chaleco", "Práctica y charla"],
    },
  },
  {
    slug: "fajardo-bio-bay-kayak",
    category: "water",
    shortname: "ikapr",
    itemId: "168561",
    cover: FS("MqeMOMmTRepXPCn9STBE"),
    gallery: [FS("MqeMOMmTRepXPCn9STBE"), U("1552083375-1447ce886485"), U("1439066615861-d1af74d74000"), U("1471922694854-ff1b63b20054")],
    place: "Fajardo",
    company: { name: "Island Kayaking Adventure", email: "info@ikapr.com", phone: "(787) 444-0059" },
    en: {
      name: "Fajardo Bioluminescent Bay Kayak",
      tag: "Glowing waters",
      duration: "~2 hrs (night)",
      short: "Paddle through one of the world's brightest bioluminescent bays after dark.",
      description: "Kayak into Laguna Grande in Fajardo, where microscopic organisms light up the water with every paddle stroke. A guided nighttime journey through mangrove channels into one of the planet's few naturally glowing bays.",
      highlights: ["Glowing bioluminescent water", "Guided night paddle", "Mangrove channel journey", "One of Earth's brightest bays"],
      included: ["Kayak & paddle", "Life vest & guide", "Headlamp"],
    },
    es: {
      name: "Kayak en la Bahía Bioluminiscente de Fajardo",
      tag: "Aguas que brillan",
      duration: "~2 hrs (noche)",
      short: "Rema por una de las bahías bioluminiscentes más brillantes del mundo al caer la noche.",
      description: "Navega en kayak hasta la Laguna Grande de Fajardo, donde organismos microscópicos iluminan el agua con cada remada. Un recorrido nocturno guiado por canales de manglar hasta una de las pocas bahías que brillan de forma natural.",
      highlights: ["Agua bioluminiscente que brilla", "Remada nocturna guiada", "Recorrido por manglares", "De las bahías más brillantes"],
      included: ["Kayak y remo", "Chaleco y guía", "Linterna frontal"],
    },
  },
  {
    slug: "rum-runners-cocktails-tour",
    category: "food",
    shortname: "sanjuanfoodtours",
    itemId: "230208",
    cover: FS("GliTBwqaTwmzWhh1nmU9"),
    gallery: [FS("GliTBwqaTwmzWhh1nmU9"), U("1518182170546-07661fd94144"), U("1596797038530-2c107229654b"), U("1534766555764-ce878a5e3a2b")],
    place: "Old San Juan",
    company: { name: "Flavors of San Juan Food & Culture Tours", email: "sanjuan@flavorsfoodtours.com", phone: "787-964-2447" },
    en: {
      name: "Rum Runners Craft Cocktails Tour",
      tag: "Sip Old San Juan",
      duration: "~3 hrs",
      short: "A guided crawl through Old San Juan's best bars, tasting Puerto Rico's signature rum cocktails.",
      description: "Discover why Puerto Rico is the rum capital of the world on this guided cocktail tour through Old San Juan. Sample craft drinks at hand-picked bars while your local guide shares the island's rum history and hidden stories.",
      highlights: ["Hand-picked Old San Juan bars", "Signature rum cocktails", "Local guide & rum history", "Small-group crawl"],
      included: ["Craft cocktail tastings", "Local guide", "Bar-to-bar walking route"],
    },
    es: {
      name: "Tour de Cócteles Artesanales Rum Runners",
      tag: "Brinda en el Viejo San Juan",
      duration: "~3 hrs",
      short: "Un recorrido guiado por los mejores bares del Viejo San Juan probando los cócteles de ron de la isla.",
      description: "Descubre por qué Puerto Rico es la capital mundial del ron en este tour de cócteles por el Viejo San Juan. Prueba bebidas artesanales en bares seleccionados mientras tu guía local comparte la historia del ron y sus secretos.",
      highlights: ["Bares selectos del Viejo San Juan", "Cócteles de ron de la isla", "Guía local e historia del ron", "Recorrido en grupo pequeño"],
      included: ["Degustación de cócteles", "Guía local", "Ruta a pie entre bares"],
    },
  },
  {
    slug: "condado-paddleboard-rental",
    category: "water",
    shortname: "aquaexperiencespr",
    itemId: "456045",
    cover: FS("NPr2pLxRjSRpnOd7bg5z"),
    gallery: [FS("NPr2pLxRjSRpnOd7bg5z"), U("1502933691298-84fc14542831"), U("1439066615861-d1af74d74000"), U("1507525428034-b723cf961d3e")],
    place: "San Juan · Condado",
    company: { name: "Aqua Experiences", email: "aquafitnessreservation@gmail.com", phone: "(787) 903-2141" },
    en: {
      name: "Condado Paddleboard Rental",
      tag: "Calm lagoon paddling",
      duration: "Hourly rental",
      short: "Rent a stand-up paddleboard and explore the calm, protected Condado Lagoon at your own pace.",
      description: "Grab a board and glide across the sheltered Condado Lagoon — calm, warm water that's ideal for first-timers and pros alike. Rent by the hour and paddle beneath the city skyline among turtles and gentle mangrove edges.",
      highlights: ["Calm, protected lagoon", "Great for beginners", "City skyline paddling", "Rent by the hour"],
      included: ["Stand-up paddleboard", "Paddle & leash", "Life vest"],
    },
    es: {
      name: "Alquiler de Paddleboard en Condado",
      tag: "Rema en aguas calmadas",
      duration: "Alquiler por hora",
      short: "Alquila una tabla de paddle y explora la tranquila Laguna del Condado a tu ritmo.",
      description: "Toma una tabla y deslízate por la protegida Laguna del Condado — agua calmada y cálida, ideal para principiantes y expertos. Alquila por hora y rema bajo el perfil de la ciudad entre tortugas y manglares.",
      highlights: ["Laguna calmada y protegida", "Ideal para principiantes", "Rema con vista a la ciudad", "Alquila por hora"],
      included: ["Tabla de paddle", "Remo y correa", "Chaleco salvavidas"],
    },
  },
  {
    slug: "condado-clear-kayak",
    category: "water",
    shortname: "aquaexperiencespr",
    itemId: "468043",
    cover: FS("YKTN6fdQqmkfRAPODxUH"),
    gallery: [FS("YKTN6fdQqmkfRAPODxUH"), U("1439066615861-d1af74d74000"), U("1552083375-1447ce886485"), U("1607153333879-c174d265f1d2")],
    place: "San Juan · Condado",
    company: { name: "Aqua Experiences", email: "aquafitnessreservation@gmail.com", phone: "(787) 903-2141" },
    en: {
      name: "Clear Kayak Tour — Condado Lagoon",
      tag: "See-through kayaks",
      duration: "~1.5 hrs",
      short: "Paddle transparent kayaks over the Condado Lagoon to spot marine life beneath you.",
      description: "Float above the water in a crystal-clear kayak and watch fish, rays and turtles glide beneath your feet. A guided tour of the Condado Lagoon that's equal parts relaxing and mesmerizing — great for families and photographers.",
      highlights: ["See-through kayak", "Marine life beneath you", "Calm Condado Lagoon", "Family-friendly"],
      included: ["Transparent kayak", "Paddle & life vest", "Guide"],
    },
    es: {
      name: "Tour en Kayak Transparente — Laguna del Condado",
      tag: "Kayaks transparentes",
      duration: "~1.5 hrs",
      short: "Rema en kayaks transparentes por la Laguna del Condado y observa la vida marina bajo ti.",
      description: "Flota sobre el agua en un kayak transparente y observa peces, rayas y tortugas deslizarse bajo tus pies. Un tour guiado por la Laguna del Condado, relajante y fascinante a la vez — ideal para familias y fotógrafos.",
      highlights: ["Kayak transparente", "Vida marina bajo ti", "Laguna del Condado tranquila", "Apto para familias"],
      included: ["Kayak transparente", "Remo y chaleco", "Guía"],
    },
  },
  {
    slug: "cave-adventure",
    category: "land",
    shortname: "geoaventuraspr",
    itemId: "569623",
    cover: U("1682687220742-aba13b6e50ba"),
    gallery: [U("1682687220742-aba13b6e50ba"), U("1520962922320-2038eebab146"), U("1441974231531-c6227db76b6e"), U("1552083375-1447ce886485")],
    place: "Puerto Rico · Karst Country",
    company: { name: "GeoAventuras PR", email: "info@geoaventuraspr.com", phone: "787-417-7723" },
    en: {
      name: "The Cave Adventure",
      tag: "Underground expedition",
      duration: "Half day",
      short: "Rappel, climb and wade through a wild river cave in Puerto Rico's karst country.",
      description: "Descend into Puerto Rico's dramatic cave systems on a guided expedition through the island's limestone karst region. Expect rappelling, rock scrambling and river crossings inside cathedral-sized caverns — a genuine adventure for the bold.",
      highlights: ["Rappel & rock scramble", "River cave crossings", "Karst limestone caverns", "For adventurous travelers"],
      included: ["Helmet & headlamp", "Harness & gear", "Certified guide"],
    },
    es: {
      name: "La Aventura de la Cueva",
      tag: "Expedición subterránea",
      duration: "Medio día",
      short: "Rapel, escalada y ríos dentro de una cueva salvaje en la zona kárstica de Puerto Rico.",
      description: "Desciende a los espectaculares sistemas de cuevas de Puerto Rico en una expedición guiada por la región kárstica de piedra caliza. Prepárate para rapel, escalada y cruces de río dentro de cavernas enormes — una aventura real para los más audaces.",
      highlights: ["Rapel y escalada", "Cruces de río en cueva", "Cavernas de piedra caliza", "Para viajeros aventureros"],
      included: ["Casco y linterna", "Arnés y equipo", "Guía certificado"],
    },
  },
];

// Unified lookup across regular partners + Salty Spirit luxury experiences.
export function getActivityBySlug(slug) {
  const regular = partners.find((p) => p.slug === slug);
  if (regular) return { ...regular, luxury: false };
  const salty = saltyExperiences.find((e) => e.slug === slug);
  if (salty) {
    return {
      ...salty,
      luxury: true,
      shortname: "saltyspiritllc",
      place: "Vieques",
      category: "luxury",
      company: {
        name: SALTY_SPIRIT.brand + " LLC",
        email: "info@salty-spirit.com",
        phone: "1 (787) 477-8664",
      },
    };
  }
  return null;
}
